<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import LaisWhatsappConnectionsAPI from 'dashboard/api/laisWhatsappConnections';
import LaisWhatsappOperationsAPI from 'dashboard/api/laisWhatsappOperations';

const props = defineProps({
  groupJid: {
    type: String,
    required: true,
  },
  inboxId: {
    type: [Number, String],
    default: null,
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const connections = ref([]);
const group = ref(null);
const isLoading = ref(false);
const loadFailed = ref(false);
const pendingAction = ref('');
const selectedParticipant = ref(null);
const directMessage = ref('');
const showDirectMessageModal = ref(false);
const showLeaveModal = ref(false);
const showConfirmModal = ref(false);

const responseData = response =>
  response?.data?.data || response?.data || response;
const asObject = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const toArray = value => {
  if (Array.isArray(value)) return value;
  return value === undefined || value === null ? [] : [value];
};
const connection = computed(() =>
  connections.value.find(
    item => Number(item.inbox_id) === Number(props.inboxId)
  )
);
const participants = computed(() => toArray(group.value?.participants));
const isMuted = computed(() => Boolean(props.conversation?.snoozed_until));
const muteLabel = computed(() =>
  isMuted.value ? t('WHATSAPP_GROUP.UNMUTE') : t('WHATSAPP_GROUP.MUTE')
);

const participantJid = participant =>
  participant?.phone_number_jid || participant?.jid || participant?.lid || '';

const displayParticipant = participant => {
  const jid = participantJid(participant);
  return (
    participant.push_name ||
    participant.name ||
    jid.replace(/@.+$/, '') ||
    t('WHATSAPP_GROUP.UNKNOWN_MEMBER')
  );
};

const normalizedJid = value =>
  String(value || '')
    .trim()
    .replace(/:\d+@/, '@')
    .replace(/@lid$/, '@s.whatsapp.net');

const isOwner = participant => {
  const owner = normalizedJid(group.value?.owner_jid);
  return [participant?.phone_number_jid, participant?.jid, participant?.lid]
    .map(normalizedJid)
    .filter(Boolean)
    .includes(owner);
};

const role = participant => {
  if (isOwner(participant) || participant.is_super_admin)
    return t('WHATSAPP_GROUP.OWNER');
  if (participant.is_admin) return t('WHATSAPP_GROUP.ADMIN');
  return t('WHATSAPP_GROUP.MEMBER');
};

const load = async () => {
  if (!props.groupJid) return;

  isLoading.value = true;
  loadFailed.value = false;
  try {
    const connectionsResponse = await LaisWhatsappConnectionsAPI.list();
    connections.value = toArray(responseData(connectionsResponse));
    if (!connection.value?.id) {
      loadFailed.value = true;
      return;
    }

    const groupResponse = await LaisWhatsappOperationsAPI.execute(
      connection.value.id,
      'get_group',
      {
        group_jid: props.groupJid,
      }
    );
    group.value = asObject(responseData(groupResponse));
  } catch (error) {
    loadFailed.value = true;
  } finally {
    isLoading.value = false;
  }
};

const execute = async (operation, payload, successMessage) => {
  if (!connection.value?.id) return false;

  pendingAction.value = operation;
  try {
    await LaisWhatsappOperationsAPI.execute(
      connection.value.id,
      operation,
      payload
    );
    useAlert(successMessage);
    return true;
  } catch (error) {
    useAlert(t('WHATSAPP_GROUP.ACTION_FAILED'));
    return false;
  } finally {
    pendingAction.value = '';
  }
};

const toggleMute = async () => {
  const wasMuted = isMuted.value;
  const action = wasMuted ? 'unmuteConversation' : 'muteConversation';
  try {
    await store.dispatch(action, props.conversation.id);
    useAlert(
      wasMuted ? t('WHATSAPP_GROUP.UNMUTED') : t('WHATSAPP_GROUP.MUTED')
    );
  } catch (error) {
    useAlert(t('WHATSAPP_GROUP.ACTION_FAILED'));
  }
};

const openDirectMessage = participant => {
  selectedParticipant.value = participant;
  directMessage.value = '';
  showDirectMessageModal.value = true;
};

const sendDirectMessage = async () => {
  const recipient = participantJid(selectedParticipant.value);
  if (!recipient || !directMessage.value.trim()) return;

  const sent = await execute(
    'send_text',
    { number: recipient, text: directMessage.value.trim() },
    t('WHATSAPP_GROUP.DIRECT_MESSAGE_SENT')
  );
  if (sent) showDirectMessageModal.value = false;
};

const requestParticipantAction = (participant, action) => {
  selectedParticipant.value = participant;
  pendingAction.value = action;
  showConfirmModal.value = true;
};

const confirmParticipantAction = async () => {
  const participant = selectedParticipant.value;
  const action = pendingAction.value;
  const targetJid = participantJid(participant);
  if (!targetJid || !action) return;

  showConfirmModal.value = false;
  pendingAction.value = '';
  const isBlock = action === 'block';
  const success = await execute(
    isBlock ? 'update_blocklist' : 'update_group_participants',
    isBlock
      ? { jid: targetJid, action: 'block' }
      : { group_jid: props.groupJid, action, participants: [targetJid] },
    isBlock
      ? t('WHATSAPP_GROUP.MEMBER_BLOCKED')
      : t('WHATSAPP_GROUP.PARTICIPANT_UPDATED')
  );
  if (success) await load();
};

const leaveGroup = async () => {
  showLeaveModal.value = false;
  const left = await execute(
    'leave_group',
    { group_jid: props.groupJid },
    t('WHATSAPP_GROUP.LEFT')
  );
  if (left) group.value = null;
};

watch(() => [props.groupJid, props.inboxId], load, { immediate: true });
</script>

<template>
  <section
    class="rounded-xl border border-n-weak bg-n-background px-3 py-4 shadow-sm"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_GROUP.TITLE') }}
        </p>
        <p v-if="group?.subject" class="mt-1 truncate text-xs text-n-slate-11">
          {{ group.subject }}
        </p>
      </div>
      <Button
        :aria-label="$t('WHATSAPP_GROUP.REFRESH')"
        color="slate"
        icon="i-ph-arrow-clockwise"
        size="sm"
        variant="faded"
        @click="load"
      />
    </div>

    <div
      v-if="isLoading && !group"
      class="py-6 text-center text-sm text-n-slate-11"
    >
      {{ $t('WHATSAPP_GROUP.LOADING') }}
    </div>
    <div
      v-else-if="loadFailed"
      class="py-5 text-center text-sm text-n-slate-11"
    >
      {{ $t('WHATSAPP_GROUP.LOAD_FAILED') }}
    </div>

    <div v-else-if="group" class="mt-4 space-y-4">
      <div class="grid grid-cols-2 gap-2">
        <div
          class="rounded-lg bg-n-slate-3 px-2 py-2 text-center dark:bg-n-slate-4"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{ group.participant_count ?? participants.length }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.PARTICIPANTS') }}
          </p>
        </div>
        <div
          class="rounded-lg bg-n-slate-3 px-2 py-2 text-center dark:bg-n-slate-4"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{
              group.is_announcement
                ? $t('WHATSAPP_GROUP.YES')
                : $t('WHATSAPP_GROUP.NO')
            }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.ANNOUNCEMENT') }}
          </p>
        </div>
        <div
          class="rounded-lg bg-n-slate-3 px-2 py-2 text-center dark:bg-n-slate-4"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{
              group.is_locked
                ? $t('WHATSAPP_GROUP.YES')
                : $t('WHATSAPP_GROUP.NO')
            }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.LOCKED') }}
          </p>
        </div>
        <div
          class="rounded-lg bg-n-slate-3 px-2 py-2 text-center dark:bg-n-slate-4"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{
              group.is_community
                ? $t('WHATSAPP_GROUP.YES')
                : $t('WHATSAPP_GROUP.NO')
            }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.COMMUNITY') }}
          </p>
        </div>
        <div
          class="rounded-lg bg-n-slate-3 px-2 py-2 text-center dark:bg-n-slate-4"
        >
          <p class="text-sm font-semibold text-n-slate-12">
            {{
              group.join_approval_required
                ? $t('WHATSAPP_GROUP.YES')
                : $t('WHATSAPP_GROUP.NO')
            }}
          </p>
          <p class="text-[11px] text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.JOIN_APPROVAL') }}
          </p>
        </div>
      </div>

      <p
        v-if="group.description"
        class="rounded-lg border border-n-weak px-3 py-2 text-xs text-n-slate-11"
      >
        {{ group.description }}
      </p>

      <dl
        v-if="
          group.member_add_mode ||
          group.parent_jid ||
          group.owner_jid ||
          group.jid ||
          group.id
        "
        class="rounded-lg border border-n-weak p-3 text-xs"
      >
        <div v-if="group.member_add_mode" class="flex justify-between gap-3">
          <dt class="text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.MEMBER_ADD_MODE') }}
          </dt>
          <dd class="text-right text-n-slate-12">
            {{ group.member_add_mode }}
          </dd>
        </div>
        <div
          v-if="group.parent_jid"
          class="mt-2 flex items-start justify-between gap-3"
        >
          <dt class="text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.PARENT_COMMUNITY') }}
          </dt>
          <dd class="break-all text-right text-n-slate-12">
            {{ group.parent_jid }}
          </dd>
        </div>
        <div
          v-if="group.jid || group.id"
          class="mt-2 flex items-start justify-between gap-3"
        >
          <dt class="text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.GROUP_ID') }}
          </dt>
          <dd class="break-all text-right text-n-slate-12">
            {{ group.jid || group.id }}
          </dd>
        </div>
        <div
          v-if="group.owner_jid"
          class="mt-2 flex items-start justify-between gap-3"
        >
          <dt class="text-n-slate-11">
            {{ $t('WHATSAPP_GROUP.OWNER_IDENTIFIER') }}
          </dt>
          <dd class="break-all text-right text-n-slate-12">
            {{ group.owner_jid }}
          </dd>
        </div>
      </dl>

      <div class="rounded-lg border border-n-weak">
        <div
          class="flex items-center justify-between border-b border-n-weak px-3 py-2"
        >
          <p class="text-xs font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_GROUP.MEMBERS_AND_PERMISSIONS') }}
          </p>
          <span class="text-[11px] text-n-slate-11">{{
            participants.length
          }}</span>
        </div>
        <div class="max-h-72 divide-y divide-n-weak overflow-y-auto">
          <div
            v-for="participant in participants"
            :key="
              participant.jid || participant.phone_number_jid || participant.lid
            "
            class="flex items-center gap-2 px-3 py-2"
          >
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-brand/10 text-xs font-semibold text-n-brand"
            >
              {{ displayParticipant(participant).slice(0, 2) }}
            </div>
            <div class="min-w-0 flex-1">
              <p class="truncate text-xs font-medium text-n-slate-12">
                {{ displayParticipant(participant) }}
              </p>
              <p class="text-[11px] text-n-slate-11">{{ role(participant) }}</p>
              <p
                v-if="participant.error"
                class="mt-0.5 text-[11px] text-n-ruby-11"
              >
                {{
                  $t('WHATSAPP_GROUP.PARTICIPANT_ERROR', {
                    code: participant.error,
                  })
                }}
              </p>
            </div>
            <div
              v-if="participantJid(participant)"
              class="flex shrink-0 items-center gap-1"
            >
              <Button
                :aria-label="$t('WHATSAPP_GROUP.SEND_DIRECT_MESSAGE')"
                color="slate"
                icon="i-ph-paper-plane-tilt"
                size="sm"
                variant="ghost"
                @click="openDirectMessage(participant)"
              />
              <template v-if="!isOwner(participant)">
                <Button
                  :aria-label="$t('WHATSAPP_GROUP.BLOCK_MEMBER')"
                  color="ruby"
                  icon="i-ph-prohibit"
                  size="sm"
                  variant="ghost"
                  @click="requestParticipantAction(participant, 'block')"
                />
                <Button
                  v-if="!participant.is_admin && !participant.is_super_admin"
                  :aria-label="$t('WHATSAPP_GROUP.PROMOTE_MEMBER')"
                  color="slate"
                  icon="i-ph-arrow-up"
                  size="sm"
                  variant="ghost"
                  @click="requestParticipantAction(participant, 'promote')"
                />
                <Button
                  v-else
                  :aria-label="$t('WHATSAPP_GROUP.DEMOTE_MEMBER')"
                  color="slate"
                  icon="i-ph-arrow-down"
                  size="sm"
                  variant="ghost"
                  @click="requestParticipantAction(participant, 'demote')"
                />
                <Button
                  :aria-label="$t('WHATSAPP_GROUP.REMOVE_MEMBER')"
                  color="ruby"
                  icon="i-ph-user-minus"
                  size="sm"
                  variant="ghost"
                  @click="requestParticipantAction(participant, 'remove')"
                />
              </template>
            </div>
          </div>
          <p
            v-if="!participants.length"
            class="px-3 py-4 text-center text-xs text-n-slate-11"
          >
            {{ $t('WHATSAPP_GROUP.NO_PARTICIPANTS') }}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap gap-2 border-t border-n-weak pt-3">
        <Button
          :label="muteLabel"
          color="slate"
          size="sm"
          variant="faded"
          @click="toggleMute"
        />
        <Button
          :label="$t('WHATSAPP_GROUP.LEAVE')"
          color="ruby"
          size="sm"
          variant="faded"
          @click="showLeaveModal = true"
        />
      </div>
    </div>

    <woot-modal v-model:show="showDirectMessageModal">
      <div class="space-y-4 p-5">
        <div>
          <p class="text-base font-semibold text-n-slate-12">
            {{ $t('WHATSAPP_GROUP.DIRECT_MESSAGE_TITLE') }}
          </p>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ displayParticipant(selectedParticipant || {}) }}
          </p>
        </div>
        <textarea
          v-model="directMessage"
          class="min-h-28 w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm"
          :placeholder="$t('WHATSAPP_GROUP.DIRECT_MESSAGE_PLACEHOLDER')"
        />
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('WHATSAPP_GROUP.CANCEL')"
            color="slate"
            variant="faded"
            @click="showDirectMessageModal = false"
          />
          <Button
            :label="$t('WHATSAPP_GROUP.SEND')"
            :disabled="!directMessage.trim()"
            :loading="pendingAction === 'send_text'"
            @click="sendDirectMessage"
          />
        </div>
      </div>
    </woot-modal>

    <woot-modal v-model:show="showConfirmModal">
      <div class="space-y-4 p-5">
        <p class="text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_GROUP.CONFIRM_MEMBER_ACTION') }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{
            $t('WHATSAPP_GROUP.CONFIRM_MEMBER_ACTION_TEXT', {
              member: displayParticipant(selectedParticipant || {}),
            })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('WHATSAPP_GROUP.CANCEL')"
            color="slate"
            variant="faded"
            @click="showConfirmModal = false"
          />
          <Button
            :label="$t('WHATSAPP_GROUP.CONFIRM')"
            color="ruby"
            @click="confirmParticipantAction"
          />
        </div>
      </div>
    </woot-modal>

    <woot-modal v-model:show="showLeaveModal">
      <div class="space-y-4 p-5">
        <p class="text-base font-semibold text-n-slate-12">
          {{ $t('WHATSAPP_GROUP.CONFIRM_LEAVE') }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{ $t('WHATSAPP_GROUP.CONFIRM_LEAVE_TEXT') }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t('WHATSAPP_GROUP.CANCEL')"
            color="slate"
            variant="faded"
            @click="showLeaveModal = false"
          />
          <Button
            :label="$t('WHATSAPP_GROUP.LEAVE')"
            color="ruby"
            :loading="pendingAction === 'leave_group'"
            @click="leaveGroup"
          />
        </div>
      </div>
    </woot-modal>
  </section>
</template>
